import { ImageOverlay } from "@components/ImageOverlay";
import { Loading } from "@components/Loading";
import { doc, updateDoc } from "@firebase/firestore";
import { useEvent } from "@helpers/useEvent";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { FirebaseContext } from "src/helpers/firebase";
import { useConfetti } from "src/helpers/useConfetti";
import { useEventNavigate } from "src/helpers/useEventNavigate";
import { useTeam } from "src/helpers/useTeam";
import { useToast } from "src/helpers/useToast";
import { Input } from "@components/Input";
import { useForm } from "react-hook-form";
import { UpIcon } from "@components/Icons/Up";
import { DownIcon } from "@components/Icons/Down";

type Props = {
  challenge: SongChallenge;
  teamId: string;
  eventId: string;
};

export const Song: FC<Props> = (props) => {
  const { id } = props.challenge;
  const { eventId, teamId } = props;
  const { t } = useTranslation("challenges");
  const { firestore } = useContext(FirebaseContext);
  const navigate = useEventNavigate();
  const [event] = useEvent();
  const [team] = useTeam();
  const [submitting, setSubmitting] = useState(false);
  const [songName, setSongName] = useState("");
  useConfetti("submit-button");
  const toast = useToast();

  const { getTranslatedValue } = useTranslatedKey();
  const title = getTranslatedValue(props.challenge, "title");
  const copy = getTranslatedValue(props.challenge, "copy");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm();

  const submit = async (songRequest: Song) => {
    if (!team) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(`Team is undefined at challenge submit step. Team: ${team}, event: ${eventId}`);
      return;
    }

    setSubmitting(true);
    try {
      const songRequests = event?.songRequests ?? [];
      const newSongRequest = {
        id: `${Date.now()}`,
        teamId,
        teamName: team.name,
        songName: songRequest.songName,
        votedTeams: [],
      };
      const newSongRequests = [...songRequests, newSongRequest];
      const eventDocRef = doc(firestore, `events/${eventId}`);
      await updateDoc(eventDocRef, { songRequests: newSongRequests });
      toast(t("songSubmitted"), "✅", "success");
      reset();
      setSongName("");
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleVote = async (songId: string) => {
    if (!team) return;

    try {
      const songRequests = event?.songRequests ?? [];
      const updatedSongRequests = songRequests.map((song) => {
        if (song.id === songId) {
          const hasVoted = song.votedTeams.includes(teamId);
          if (hasVoted) {
            // Remove the vote
            return {
              ...song,
              votedTeams: song.votedTeams.filter((id) => id !== teamId),
            };
          } else {
            // Add the vote
            return {
              ...song,
              votedTeams: [...song.votedTeams, teamId],
            };
          }
        }
        return song;
      });

      const eventDocRef = doc(firestore, `events/${eventId}`);
      await updateDoc(eventDocRef, { songRequests: updatedSongRequests });

      // Show different toast messages for adding/removing votes
      const hasVoted = songRequests.find((s) => s.id === songId)?.votedTeams.includes(teamId);
      if (!hasVoted) {
        toast(t("voteSubmitted"), "👍", "success");
      }
    } catch (error) {
      toast(t("problemOccured"), "🤷‍♂️", "error");
      console.error(error);
    }
  };

  const sortedSongRequests = useMemo(() => {
    if (!event?.songRequests) return [];
    return [...event.songRequests].sort((a, b) => b.votedTeams.length - a.votedTeams.length);
  }, [event?.songRequests]);

  return (
    <>
      <h1 className="mx-4 mt-16 text-center text-4xl font-semibold">{title}</h1>
      <div className="mt-4 flex flex-col items-center">
        <div className="flex w-full max-w-xl flex-col justify-center">
          <div className="mt-12 w-full">
            <h2 className="mb-4 text-center text-xl">{t("requestedSongs")} 🎶</h2>
            <div className="flex w-full flex-col gap-4 px-6">
              {sortedSongRequests.map((song, index) => (
                <div className="flex w-full flex-col" key={song.id}>
                  <div className="min-w-2xl flex w-full flex-col rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-3">
                    <div className="flex w-full items-center">
                      <div
                        className={`mr-7 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full ${
                          index === 0 ? "bg-yellow-900" : "bg-neutral"
                        } font-semibold text-primary-content`}
                      >
                        {index + 1}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <p className="break-words font-semibold">{song.songName}</p>
                        <p className="mt-2 break-words text-xs font-light text-base-content/70">
                          {t("requestedBy")}: {song.teamName}
                        </p>
                      </div>
                      <button onClick={() => handleToggleVote(song.id)} className="mr-2">
                        <div className="flex justify-center">
                          <UpIcon className={`h-8 w-8 ${song.votedTeams.includes(teamId) ? "" : "text-base-300"}`} />
                        </div>
                        <div className="mt-2 text-xs">{`${t("votes")} ${song.votedTeams.length}`}</div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={handleSubmit(submit)} className="w-full px-6 pt-12">
            <Input
              label={t("songName")}
              name="songName"
              placeholder={t("enterSongName")}
              errors={errors}
              register={register}
              required
              requiredMessage={t("songNameRequired")}
            />
            <div className="mb-12 mt-4 flex justify-center">
              <button type="submit" disabled={submitting} className={`btn-primary btn ${submitting ? "loading" : ""}`}>
                {t("request")}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};
